import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Sparkles, Wand2, Trash2, Truck, Save, RefreshCw, Send, FileText, PenTool, Users, Copy, Check, Gift, HeartHandshake, ImagePlus, Crown, Image as ImageIcon, Mic, Square, Play, Pause, QrCode, UserPlus, Heart, Gem, Lightbulb, Loader2, Moon, Laugh, Leaf, PartyPopper, Sparkle } from "lucide-react";
import MagicWand from "@/components/MagicWand";
import GiftPreview from "@/components/GiftPreview";
import SignaturePad from "@/components/SignaturePad";
import UpgradeModal from "@/components/UpgradeModal";
import BoutiqueCheckoutModal from "@/components/BoutiqueCheckoutModal";
import LivingCapsuleModal from "@/components/LivingCapsuleModal";
import PurchaseSealOverlay from "@/components/PurchaseSealOverlay";
import SealBadge from "@/components/SealBadge";
import CelestialCard from "@/components/CelestialCard";
import DirectorDebugTag from "@/components/DirectorDebugTag";
import { isPremiumUnlocked } from "@/lib/upgrade";
import { QRCodeCanvas } from "qrcode.react";

const TONES = ["warm and heartfelt", "funny and playful", "elegant and poetic", "sincere and simple", "celebratory and bold"];

// The Sovereign Library — Vibe Selector. Open to ALL tiers (no premium gate); "celestial"
// needs the recipient's birthday on file to resolve a zodiac sign.
const VIBES = [
  { id: "", label: "Classic Maison", icon: Sparkles },
  { id: "funny", label: "Funny", icon: Laugh },
  { id: "seasonal", label: "Seasonal", icon: Leaf },
  { id: "holiday", label: "Holiday", icon: PartyPopper },
  { id: "regal", label: "Regal", icon: Crown },
  { id: "celestial", label: "Celestial Insight", icon: Moon },
];

// Client-side downscale before upload (max ~2000px longest side, JPEG ~0.85).
const downscaleImage = (file, maxPx = 2000, quality = 0.85) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) { height = Math.round(height * (maxPx / width)); width = maxPx; }
        else { width = Math.round(width * (maxPx / height)); height = maxPx; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality).split(",")[1]);
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const Cards = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [lovedOnes, setLovedOnes] = useState([]);
  const [cards, setCards] = useState([]);
  const [lovedOneId, setLovedOneId] = useState(params.get("loved_one_id") || "");
  const [occasion, setOccasion] = useState(params.get("occasion") || "Birthday");
  const [date, setDate] = useState(params.get("date") || "");
  const [tone, setTone] = useState(TONES[0]);
  const [vibe, setVibe] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateCategory, setTemplateCategory] = useState("");
  const [usingTemplateId, setUsingTemplateId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [editMsg, setEditMsg] = useState("");
  const [previewId, setPreviewId] = useState(null);
  const [credits, setCredits] = useState(null);
  const [charities, setCharities] = useState([]);
  const [giftType, setGiftType] = useState("none");
  const [giftAmount, setGiftAmount] = useState(25);
  const [giftNote, setGiftNote] = useState("");
  const [charityEin, setCharityEin] = useState("");
  const [giftSaved, setGiftSaved] = useState(false);
  const [cosignLink, setCosignLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [sigSaved, setSigSaved] = useState(false);
  const [styleMode, setStyleMode] = useState("ai");
  const [studioBusy, setStudioBusy] = useState("");
  // Audio Keepsake state
  const [recording, setRecording] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioSaving, setAudioSaving] = useState(false);
  const [sealUrl, setSealUrl] = useState("");
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioElRef = useRef(null);
  const audioUploadRef = useRef(null);
  const photoRef = useRef(null);
  const padRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const premiumUnlocked = isPremiumUnlocked(user);
  const [upgradeGate, setUpgradeGate] = useState(null); // {tier, feature}
  const [boutiqueModal, setBoutiqueModal] = useState(false);
  const [boutiqueCardId, setBoutiqueCardId] = useState("");
  const [capsuleCard, setCapsuleCard] = useState(null);
  const [qrCard, setQrCard] = useState(null);
  const [materialPreview, setMaterialPreview] = useState("");
  // Bespoke Mashup — instant client-side blend preview of the raw uploaded photo over the
  // current background, so the sender sees a "printed on the material" look immediately
  // while the real server-side composite bakes.
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  // The Purchase Seal — success overlay shown the instant a physical mailing purchase lands.
  const [purchaseSealCard, setPurchaseSealCard] = useState(null);
  // Courtesy Ledger (Silver Seal, one-time recipients outside Loved Ones)
  const [recipientMode, setRecipientMode] = useState("loved_one"); // "loved_one" | "courtesy"
  const [courtesyContacts, setCourtesyContacts] = useState([]);
  const [courtesyContactId, setCourtesyContactId] = useState("");
  const [addingCourtesy, setAddingCourtesy] = useState(false);
  const [newCourtesy, setNewCourtesy] = useState({ name: "", email: "", address_line1: "", address_city: "", address_state: "", address_zip: "" });
  const [savingCourtesy, setSavingCourtesy] = useState(false);
  // Wishlist Highlights (premium)
  const [wishlist, setWishlist] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const applyStudioResult = (data) => {
    setStyleMode(data.style_mode);
    setResult((r) => (r ? { ...r, image_base64: data.image_base64, image_thumb_base64: data.image_thumb_base64, style_mode: data.style_mode } : r));
    // Real bake landed — retire the instant client-side blend preview.
    setPhotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file || !result) return;
    // Bespoke Mashup — show an instant local blend preview (mix-blend-mode: luminosity) over
    // the current background so it reads as printed on the material right away, while the
    // real composite bakes server-side.
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setStudioBusy("photo"); setError("");
    try {
      const b64 = await downscaleImage(file);
      const res = await api.post(`/cards/${result.id}/photo`, { image_base64: b64 });
      applyStudioResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not process the photo.");
      setPhotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    } finally { setStudioBusy(""); if (photoRef.current) photoRef.current.value = ""; }
  };

  const chooseStyle = async (mode) => {
    if (!result) return;
    if (mode === "mashup" && !premiumUnlocked) {
      setUpgradeGate({ tier: "credit_pack", feature: "Bespoke Mashup" });
      return;
    }
    setStudioBusy(mode); setError("");
    try {
      const res = await api.put(`/cards/${result.id}/style`, { style_mode: mode });
      applyStudioResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not apply style.");
    } finally { setStudioBusy(""); }
  };

  const refreshStyle = async (material) => {
    if (!result) return;
    setStudioBusy("refresh"); setError("");
    try {
      const res = await api.post(`/cards/${result.id}/refresh-style`, material ? { material } : {});
      applyStudioResult(res.data);
      if (material) setResult((r) => (r ? { ...r, material } : r));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not refresh style.");
    } finally { setStudioBusy(""); }
  };

  // Material-First Generation — clicking Foil/Glass shows the instant CSS sheen (3-second
  // sensory hook) AND, if the artwork doesn't already have that material baked in, triggers a
  // real regeneration so the texture is physically in the pixels, not just an overlay.
  const applyMaterial = async (m) => {
    const next = materialPreview === m ? "" : m;
    setMaterialPreview(next);
    if (next && result?.material !== next) {
      await refreshStyle(next);
    }
  };

  // ---- Audio Keepsake ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (audioElRef.current) audioElRef.current.src = URL.createObjectURL(blob);
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(blob); });
        await saveAudio(b64, "audio/webm");
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
      // auto-stop at 30s
      setTimeout(() => { if (mediaRecRef.current && mediaRecRef.current.state === "recording") mediaRecRef.current.stop(); }, 30000);
    } catch (e) {
      setError("Microphone access is required to record. You can also upload an MP3/WAV.");
    }
  };
  const stopRecording = () => { if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop(); setRecording(false); };

  const uploadAudioFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !result) return;
    if (file.size > 5 * 1024 * 1024) { setError("Audio too large — keep it under 5MB."); return; }
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    if (audioElRef.current) audioElRef.current.src = URL.createObjectURL(file);
    await saveAudio(b64, file.type || "audio/mpeg");
    if (audioUploadRef.current) audioUploadRef.current.value = "";
  };

  const saveAudio = async (b64, mime) => {
    if (!result) return;
    setAudioSaving(true); setError("");
    try {
      await api.post(`/cards/${result.id}/audio`, { audio_base64: b64, mime });
      setAudioReady(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save audio.");
    } finally { setAudioSaving(false); }
  };

  const generateSeal = async () => {
    if (!result) return;
    if (!premiumUnlocked) {
      setUpgradeGate({ tier: "credit_pack", feature: "Royal Voice (the Sovereign Seal)" });
      return;
    }
    setStudioBusy("seal"); setError("");
    try {
      const res = await api.post(`/cards/${result.id}/sovereign-seal`);
      setSealUrl(res.data.experience_url);
    } catch (err) {
      if (err?.response?.status === 403) {
        setUpgradeGate({ tier: "credit_pack", feature: "Royal Voice (the Sovereign Seal)" });
      } else setError(err?.response?.data?.detail || "Could not generate the Seal.");
    } finally { setStudioBusy(""); }
  };


  const load = async () => {
    const [lo, cs, cr, ch, cc, tpl] = await Promise.all([
      api.get("/loved-ones"), api.get("/cards"), api.get("/credits"), api.get("/charities"), api.get("/courtesy"),
      api.get("/templates"),
    ]);
    setLovedOnes(lo.data);
    setCards(cs.data);
    setCredits(cr.data);
    setCharities(ch.data.charities);
    setCourtesyContacts(cc.data);
    setTemplates(tpl.data.templates || []);
    if (!lovedOneId && lo.data.length) setLovedOneId(lo.data[0].id);
    if (!courtesyContactId && cc.data.length) setCourtesyContactId(cc.data[0].id);
  };
  // Intentional mount-only fetch (populates initial dropdowns/lists); NOT re-run on
  // lovedOneId/courtesyContactId change to avoid refetching on every selection.
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Living Cash — confirm a cash-gift Stripe Checkout redirect (success/cancel) coming back
  // from stripe_service.create_cash_gift_checkout's success_url.
  useEffect(() => {
    const giftStatus = params.get("gift_status");
    const sessionId = params.get("session_id");
    const cardId = params.get("card_id");
    if (giftStatus === "success" && sessionId && cardId) {
      (async () => {
        try {
          const res = await api.get(`/cards/${cardId}/gift/verify/${sessionId}`);
          if (res.data.funded) { setGiftSaved(true); setTimeout(() => setGiftSaved(false), 3000); }
          await load();
        } catch (e) { console.error("Cash gift verify failed:", e); }
        window.history.replaceState({}, "", "/cards");
      })();
    } else if (giftStatus === "cancel") {
      window.history.replaceState({}, "", "/cards");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const addCourtesyContact = async () => {
    // The Complete Form — Name, Email (digital delivery/reciprocity), and full mailing
    // address are all required for a One-Time Courtesy send. This data lives ONLY in the
    // Courtesy Ledger (db.courtesy_contacts) — it is NEVER added to the permanent Loved Ones
    // roster; it's scoped to this send and future one-time sends only.
    if (!newCourtesy.name.trim()) { setError("Add a name for the courtesy contact."); return; }
    if (!newCourtesy.email.trim()) { setError("Recipient email is required for digital delivery & reciprocity."); return; }
    if (!newCourtesy.address_line1.trim() || !newCourtesy.address_city.trim() || !newCourtesy.address_state.trim() || !newCourtesy.address_zip.trim()) {
      setError("Full mailing address (street, city, state, zip) is required for Lob physical mailing.");
      return;
    }
    setSavingCourtesy(true); setError("");
    try {
      const res = await api.post("/courtesy", {
        name: newCourtesy.name,
        email: newCourtesy.email,
        address: {
          address_line1: newCourtesy.address_line1, address_city: newCourtesy.address_city,
          address_state: newCourtesy.address_state, address_zip: newCourtesy.address_zip, address_country: "US",
        },
      });
      setCourtesyContacts((prev) => [res.data, ...prev]);
      setCourtesyContactId(res.data.id);
      setAddingCourtesy(false);
      setNewCourtesy({ name: "", email: "", address_line1: "", address_city: "", address_state: "", address_zip: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add the courtesy contact.");
    } finally { setSavingCourtesy(false); }
  };

  const fetchWishlist = async () => {
    if (!lovedOneId) return;
    if (!premiumUnlocked) { setUpgradeGate({ tier: "credit_pack", feature: "Wishlist Highlights" }); return; }
    setWishlistLoading(true); setWishlist(null);
    try {
      const res = await api.post(`/loved-ones/${lovedOneId}/wishlist-highlights`);
      setWishlist(res.data.ideas || []);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "upgrade_required") setUpgradeGate({ tier: d.upgrade, feature: "Wishlist Highlights" });
      else setError(typeof d === "string" ? d : "Could not fetch wishlist ideas.");
    } finally { setWishlistLoading(false); }
  };

  const saveGift = async () => {
    if (!result) return;
    // Gate Removal — Cash Gifting is now open to every resident, regardless of tier.
    try {
      const res = await api.put(`/cards/${result.id}/gift`, {
        gift_type: giftType, amount: parseFloat(giftAmount) || 0, charity_ein: giftType === "charity" ? charityEin : null,
        note: giftType === "cash" ? giftNote : null,
      });
      // Living Cash — a real charge is required before the gift is real. Stripe redirects
      // out; on return, load() + the ?gift_status handler below confirms + shows it as funded.
      if (res.data.mode === "live" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setGiftSaved(true);
      setTimeout(() => setGiftSaved(false), 2000);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "upgrade_required") setUpgradeGate({ tier: d.upgrade, feature: "Global Cash Gifting" });
      else setError((d && typeof d === "object" ? d.message : d) || "Could not attach the gift.");
    }
  };

  const saveSignature = async () => {
    if (!result) return;
    const img = padRef.current?.getDataURL();
    if (!img) { setError("Add your signature first."); return; }
    await api.put(`/cards/${result.id}/signature`, { image_base64: img, role: "primary" });
    setSigSaved(true);
    setTimeout(() => setSigSaved(false), 2000);
  };

  const requestCosign = async () => {
    if (!result) return;
    const res = await api.post(`/cards/${result.id}/request-cosign`);
    setCosignLink(res.data.cosign_url);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(cosignLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generate = async () => {
    setError(""); setResult(null); setWishlist(null); setMaterialPreview("");
    // Zero-Gap: fully reset per-card transient Studio state so nothing from a PREVIOUS card
    // (gift note, audio "Attached" badge, cosign link, seal QR, etc.) leaks into a new one.
    setGiftType("none"); setGiftAmount(25); setGiftNote(""); setCharityEin(""); setGiftSaved(false);
    setAudioReady(false); setSealUrl(""); setCosignLink(""); setCopied(false); setSigSaved(false);
    setPhotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    if (recipientMode === "loved_one" && !lovedOneId) { setError("Please select a loved one."); return; }
    if (recipientMode === "courtesy" && !courtesyContactId) { setError("Add or select a courtesy contact first."); return; }
    setGenerating(true);
    try {
      const payload = recipientMode === "loved_one"
        ? { loved_one_id: lovedOneId, occasion, date: date || new Date().toISOString().slice(0, 10), tone, vibe: vibe || null }
        : { courtesy_contact_id: courtesyContactId, occasion, date: date || new Date().toISOString().slice(0, 10), tone, vibe: vibe || null };
      const res = await api.post("/cards/generate", payload);
      setResult(res.data);
      setEditMsg(res.data.message);
      setStyleMode(res.data.style_mode || "ai");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || "Card generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const pickTemplate = async (templateId) => {
    setError("");
    // Zero-Gap: same full reset as generate() — a Maison Classic is still a brand-new card.
    setGiftType("none"); setGiftAmount(25); setGiftNote(""); setCharityEin(""); setGiftSaved(false);
    setAudioReady(false); setSealUrl(""); setCosignLink(""); setCopied(false); setSigSaved(false);
    setMaterialPreview("");
    setPhotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    if (recipientMode === "loved_one" && !lovedOneId) { setError("Please select a loved one first."); return; }
    if (recipientMode === "courtesy" && !courtesyContactId) { setError("Add or select a courtesy contact first."); return; }
    setUsingTemplateId(templateId);
    try {
      const payload = recipientMode === "loved_one"
        ? { template_id: templateId, loved_one_id: lovedOneId, occasion, date: date || new Date().toISOString().slice(0, 10) }
        : { template_id: templateId, courtesy_contact_id: courtesyContactId, occasion, date: date || new Date().toISOString().slice(0, 10) };
      const res = await api.post("/cards/from-template", payload);
      setResult(res.data);
      setEditMsg(res.data.message);
      setStyleMode(res.data.style_mode || "ai");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not use that template. Please try again.");
    } finally {
      setUsingTemplateId("");
    }
  };

  const saveMessage = async (card) => {
    await api.put(`/cards/${card.id}/message`, { message: editMsg });
    setResult({ ...card, message: editMsg });
    await load();
  };

  // Studio UX — Auto-Save: the message is persisted to the database the INSTANT the sender
  // clicks Preview or Send, so no edit is ever lost even if they never hit "Save edits".
  const autoSaveMessage = async () => {
    if (!result) return;
    try {
      await api.put(`/cards/${result.id}/message`, { message: editMsg });
      setResult((r) => (r ? { ...r, message: editMsg } : r));
    } catch (e) {
      console.error("Auto-Save of message failed (non-blocking):", e);
    }
  };

  const approve = async (card, method) => {
    try {
      const res = await api.post(`/cards/${card.id}/approve`, { delivery_method: method });
      await load();
      if (method === "digital") {
        alert("Delivered digitally! View it under Sent with Love.\n\nYour Maison artifact is a Living Capsule. You may add future sentiments, voices, and memories at any time through your Sentiment Ledger.");
      } else {
        // The Purchase Seal — an immediate, rewarding Success Overlay the moment the physical
        // mailing purchase (credit or $9.99) is actually applied, instead of a plain alert().
        setPurchaseSealCard(card);
      }
      return res.data;
    } catch (err) {
      if (err?.response?.status === 402) {
        // Boutique, 0 mailing credits — open the $9.99 checkout + One-Click Upgrade hook.
        setBoutiqueCardId(card.id);
        setBoutiqueModal(true);
        return;
      }
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "upgrade_required") {
        setUpgradeGate({ tier: detail.upgrade, feature: detail.message || "This feature" });
        return;
      }
      alert(detail || "Could not send card.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this card?")) return;
    await api.delete(`/cards/${id}`);
    if (result?.id === id) setResult(null);
    await load();
  };

  return (
    <AppShell>
      <div className="mb-8">
        <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">
          Design Studio <Wand2 className="w-7 h-7 text-primary" />
        </h1>
        <p className="text-muted-foreground">Craft a bespoke card — message and illustration — in seconds.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Composer */}
        <div className="glass rounded-2xl p-6">
          <h2 className="font-serif text-2xl mb-4">Design your card</h2>
          <div className="space-y-4">
            {/* Recipient mode: Loved One (Gold Seal) vs Courtesy Ledger (Silver Seal, one-time) */}
            <div className="flex gap-2" data-testid="recipient-mode-toggle">
              <button type="button" onClick={() => setRecipientMode("loved_one")}
                className={`flex-1 text-sm rounded-lg py-2 border flex items-center justify-center gap-1.5 ${recipientMode === "loved_one" ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
                <Heart className="w-3.5 h-3.5" /> Loved One
              </button>
              <button type="button" data-testid="recipient-mode-courtesy" onClick={() => { setRecipientMode("courtesy"); setAddingCourtesy(true); }}
                className={`flex-1 text-sm rounded-lg py-2 border flex items-center justify-center gap-1.5 ${recipientMode === "courtesy" ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
                <Gem className="w-3.5 h-3.5" /> Courtesy (one-time)
              </button>
            </div>

            {recipientMode === "loved_one" ? (
              <div>
                <label className="text-sm text-muted-foreground">Loved one</label>
                <select data-testid="card-loved-one" value={lovedOneId} onChange={(e) => setLovedOneId(e.target.value)}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">Select…</option>
                  {lovedOnes.map((lo) => <option key={lo.id} value={lo.id}>{lo.name}</option>)}
                </select>
                {lovedOneId && (
                  <div className="mt-2">
                    <button type="button" data-testid="wishlist-highlights-btn" onClick={fetchWishlist} disabled={wishlistLoading}
                      className="text-xs flex items-center gap-1.5 text-primary hover:underline disabled:opacity-60">
                      {wishlistLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
                      Wishlist Highlights {!premiumUnlocked && <Crown className="w-3 h-3 gold-text" />}
                    </button>
                    <DirectorDebugTag requiredTier="Credit Pack" />
                    {wishlist && (
                      <ul className="mt-2 space-y-1.5" data-testid="wishlist-ideas">
                        {wishlist.map((idea, i) => (
                          <li key={i} className="text-xs surface rounded-lg px-3 py-2 flex items-start gap-1.5"><Sparkles className="w-3 h-3 gold-text mt-0.5 shrink-0" /> {idea}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="courtesy-section">
                <label className="text-sm text-muted-foreground flex items-center gap-1.5"><SealBadge tier="silver" size={16} /> Courtesy Ledger — Silver Seal, Maison Guest Note</label>
                {courtesyContacts.length > 0 && !addingCourtesy && (
                  <select data-testid="courtesy-contact-select" value={courtesyContactId} onChange={(e) => setCourtesyContactId(e.target.value)}
                    className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50">
                    {courtesyContacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                {!addingCourtesy ? (
                  <button type="button" data-testid="add-courtesy-btn" onClick={() => setAddingCourtesy(true)}
                    className="mt-2 text-xs flex items-center gap-1.5 text-primary hover:underline"><UserPlus className="w-3.5 h-3.5" /> Add a courtesy contact</button>
                ) : (
                  <div className="mt-2 surface rounded-lg p-3 space-y-2 slide-down" data-testid="one-time-courtesy-form">
                    <p className="text-[11px] text-muted-foreground">One-Time Send — this recipient is tracked in your Courtesy Ledger only; they&apos;re never added to your permanent Loved Ones roster.</p>
                    <input data-testid="courtesy-name" required placeholder="Full name *" value={newCourtesy.name} onChange={(e) => setNewCourtesy({ ...newCourtesy, name: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                    <input data-testid="courtesy-email" required type="email" placeholder="Recipient email * (digital delivery & reciprocity)" value={newCourtesy.email} onChange={(e) => setNewCourtesy({ ...newCourtesy, email: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                    <input data-testid="courtesy-address-line1" required placeholder="Street address *" value={newCourtesy.address_line1} onChange={(e) => setNewCourtesy({ ...newCourtesy, address_line1: e.target.value })}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                    <div className="grid grid-cols-3 gap-2">
                      <input data-testid="courtesy-address-city" required placeholder="City *" value={newCourtesy.address_city} onChange={(e) => setNewCourtesy({ ...newCourtesy, address_city: e.target.value })}
                        className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                      <input data-testid="courtesy-address-state" required placeholder="State *" value={newCourtesy.address_state} onChange={(e) => setNewCourtesy({ ...newCourtesy, address_state: e.target.value })}
                        className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                      <input data-testid="courtesy-address-zip" required placeholder="Zip *" value={newCourtesy.address_zip} onChange={(e) => setNewCourtesy({ ...newCourtesy, address_zip: e.target.value })}
                        className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" data-testid="save-courtesy-btn" onClick={addCourtesyContact} disabled={savingCourtesy}
                        className="flex-1 text-sm bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90 disabled:opacity-60">{savingCourtesy ? "Saving…" : "Save contact"}</button>
                      <button type="button" onClick={() => setAddingCourtesy(false)} className="flex-1 text-sm surface rounded-lg py-2 text-muted-foreground">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Occasion</label>
                <input data-testid="card-occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Date</label>
                <input data-testid="card-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50" />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Tone</label>
              <select data-testid="card-tone" value={tone} onChange={(e) => setTone(e.target.value)}
                className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50">
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* The Sovereign Library — Vibe Selector (Funny/Seasonal/Holiday/Regal/Celestial).
                Open to ALL tiers — no premium gate, no Crown lock icon. */}
            <div>
              <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Sparkle className="w-3.5 h-3.5 gold-text" /> Vibe
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-1" data-testid="vibe-selector">
                {VIBES.map((v) => {
                  const Icon = v.icon;
                  const needsBirthday = v.id === "celestial" && recipientMode === "loved_one"
                    && !lovedOnes.find((l) => l.id === lovedOneId)?.zodiac;
                  return (
                    <button key={v.id || "classic"} type="button" data-testid={`vibe-${v.id || "classic"}`}
                      onClick={() => setVibe(v.id)}
                      title={needsBirthday ? "Add this person's birthday first to unlock Celestial Insight" : v.label}
                      className={`text-[11px] rounded-lg py-2 px-1 border flex flex-col items-center gap-1 ${vibe === v.id ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground hover:border-primary/30"}`}>
                      <Icon className="w-3.5 h-3.5" /> {v.label}
                    </button>
                  );
                })}
              </div>
              {vibe === "celestial" && recipientMode === "loved_one" && (() => {
                const lo = lovedOnes.find((l) => l.id === lovedOneId);
                if (lo?.zodiac) {
                  return <CelestialCard zodiac={lo.zodiac} lovedOneId={lo.id} size="sm" className="mt-2" />;
                }
                return (
                  <p className="text-xs text-red-400 mt-1.5" data-testid="zodiac-missing-hint">
                    Add {lo?.name || "this person"}&apos;s date of birth on their profile first — Celestial Insight needs it to find their zodiac sign.
                  </p>
                );
              })()}
              {/* Vibe Clarity — Classic Maison is intentionally understated; nudge toward the
                  more visually distinct vibes without forcing anyone to pick one. */}
              {!vibe && (
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1" data-testid="vibe-clarity-tip">
                  <Sparkle className="w-3 h-3 gold-text shrink-0" /> Select a Vibe for more vibrant textures and colors.
                </p>
              )}
            </div>

            {error && <div data-testid="card-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}

            <button data-testid="generate-card-btn" onClick={generate} disabled={generating}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg py-3 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
              {generating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Crafting your card…</> : <><Sparkles className="w-4 h-4" /> Generate Card</>}
            </button>
            {generating && <p className="text-xs text-center text-muted-foreground">Composing a heartfelt message and painting the artwork — this can take ~20s.</p>}

            {/* Maison Classics — high-velocity template library, no AI wait */}
            {templates.length > 0 && (
              <div className="mt-2" data-testid="template-library">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-muted-foreground flex items-center gap-1.5"><Gem className="w-3.5 h-3.5 gold-text" /> Maison Classics — instant send, no wait</label>
                  <select data-testid="template-category-filter" value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}
                    className="text-xs bg-input border border-border rounded-lg px-2 py-1">
                    <option value="">All</option>
                    <option value="funny">Funny</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="holiday">Holiday</option>
                    <option value="regal">Regal</option>
                    <option value="celestial">Celestial</option>
                  </select>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1">
                  {templates.filter((t) => !templateCategory || t.category === templateCategory).map((t) => (
                    <button key={t.id} type="button" data-testid="template-item" onClick={() => pickTemplate(t.id)}
                      disabled={!!usingTemplateId} title={t.zodiac_sign ? `${t.title} (${t.zodiac_sign})` : t.title}
                      className="relative rounded-lg overflow-hidden border border-border hover:border-primary/50 disabled:opacity-60 aspect-square">
                      <img src={`data:image/png;base64,${t.image_thumb_base64}`} alt={t.title} className="w-full h-full object-cover" />
                      {usingTemplateId === t.id && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-4 h-4 text-white animate-spin" /></div>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="glass rounded-2xl p-6">
          <h2 className="font-serif text-2xl mb-4">Preview</h2>
          {result ? (
            <div data-testid="card-preview" className="animate-fade-up">
              <div className="rounded-xl overflow-hidden border border-border mb-3 relative" data-testid="material-preview-frame">
                <img src={`data:image/png;base64,${result.image_base64}`} alt="Card artwork" className="w-full h-64 object-cover" />
                {/* Bespoke Mashup — instant client-side blend preview: the raw uploaded photo
                    luminosity-blended over the current background so it reads as printed on
                    the material, not pasted on top, while the real bake completes. */}
                {photoPreviewUrl && (
                  <img src={photoPreviewUrl} alt="" data-testid="mashup-blend-preview"
                    className="absolute inset-0 w-full h-full object-cover mashup-blend-preview" />
                )}
                {materialPreview && (
                  <div className={`absolute inset-0 pointer-events-none ${materialPreview === "foil" ? "material-sheen-foil" : "material-sheen-glass"}`} data-testid={`material-sheen-${materialPreview}`} />
                )}
                <div className="absolute top-2 right-2" title={result.seal_tier === "gold" ? "Gold Seal" : "Silver Seal"}>
                  <SealBadge tier={result.seal_tier || "gold"} size={44} />
                </div>
              </div>

              {/* Material Preview — a 3-second sensory hook: watch light reflect off the
                  digital artifact in real-time, justifying the $9.99 price point. Also
                  triggers a real regeneration so Foil/Glass is baked into the actual art. */}
              <div className="flex items-center gap-2 mb-4" data-testid="material-preview-toggle">
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 gold-text" /> Material Preview:</span>
                {["foil", "glass"].map((m) => (
                  <button key={m} type="button" data-testid={`material-preview-${m}`} disabled={studioBusy === "refresh"}
                    onClick={() => applyMaterial(m)}
                    className={`text-xs rounded-full px-3 py-1 border capitalize flex items-center gap-1 ${materialPreview === m ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground hover:border-primary/30"} disabled:opacity-60`}>
                    {studioBusy === "refresh" && materialPreview === m ? <><Loader2 className="w-3 h-3 animate-spin" /> Baking…</> : m}
                  </button>
                ))}
              </div>

              {result.recipient_type === "courtesy" && (
                <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"><SealBadge tier="silver" size={14} /> Silver Seal — Courtesy send</p>
              )}

              {/* Creative Studio: style selection + photo upload + refresh */}
              <div className="surface rounded-xl p-3 mb-4" data-testid="creative-studio">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-primary" /> Creative Studio</span>
                  {styleMode !== "photo" && (
                    <button data-testid="refresh-style-btn" onClick={() => refreshStyle()} disabled={!!studioBusy}
                      className="text-xs flex items-center gap-1.5 text-primary hover:underline disabled:opacity-60">
                      <RefreshCw className={`w-3.5 h-3.5 ${studioBusy === "refresh" ? "animate-spin" : ""}`} /> Refresh Style
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[["ai", "AI Only", Sparkles], ["photo", "Photo Only", ImagePlus], ["mashup", "Bespoke Mashup", Crown]].map(([m, label, Icon]) => (
                    <button key={m} data-testid={`style-${m}`} onClick={() => chooseStyle(m)} disabled={!!studioBusy}
                      className={`text-xs rounded-lg py-2 border flex flex-col items-center gap-1 ${styleMode === m ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"} ${m === "mashup" && !premiumUnlocked ? "relative" : ""}`}>
                      <Icon className="w-4 h-4" />
                      {label}
                      {m === "mashup" && !premiumUnlocked && <span className="text-[9px] gold-text">Premium</span>}
                    </button>
                  ))}
                </div>
                <button data-testid="upload-photo-btn" onClick={() => photoRef.current?.click()} disabled={!!studioBusy}
                  className="w-full text-sm flex items-center justify-center gap-1.5 surface rounded-lg py-2 hover:border-primary/40 disabled:opacity-60">
                  {studioBusy === "photo" ? <><RefreshCw className="w-4 h-4 animate-spin" /> Processing…</> : <><ImagePlus className="w-4 h-4" /> Upload a Photo</>}
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
                {studioBusy && studioBusy !== "photo" && <p className="text-[11px] text-center text-muted-foreground mt-1.5">Working on your {studioBusy === "mashup" ? "bespoke mashup" : "style"}… (~15s)</p>}
              </div>

              {/* Audio Keepsake */}
              <div className="surface rounded-xl p-3 mb-4" data-testid="audio-studio">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5"><Mic className="w-4 h-4 text-primary" /> Audio Keepsake</span>
                  {audioReady && <span className="text-xs text-secondary flex items-center gap-1"><Check className="w-3 h-3" /> Attached</span>}
                </div>
                <div className="flex gap-2">
                  {!recording ? (
                    <button data-testid="record-audio-btn" onClick={startRecording} disabled={audioSaving}
                      className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-lg py-2 hover:border-primary/40 disabled:opacity-60">
                      <Mic className="w-4 h-4" /> Record (30s)
                    </button>
                  ) : (
                    <button data-testid="stop-audio-btn" onClick={stopRecording}
                      className="flex-1 text-sm flex items-center justify-center gap-1.5 bg-red-500/15 text-red-500 rounded-lg py-2 gold-pulse-ring">
                      <Square className="w-4 h-4" /> Stop
                    </button>
                  )}
                  <button data-testid="upload-audio-btn" onClick={() => audioUploadRef.current?.click()} disabled={audioSaving || recording}
                    className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-lg py-2 hover:border-primary/40 disabled:opacity-60">
                    {audioSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload MP3/WAV
                  </button>
                  <input ref={audioUploadRef} type="file" accept="audio/*" className="hidden" onChange={uploadAudioFile} data-testid="audio-input" />
                </div>
                <audio ref={audioElRef} controls className={`w-full mt-2 ${audioReady ? "" : "hidden"}`} data-testid="audio-preview" />

                {/* Sovereign Seal (Royal) */}
                {audioReady && (
                  <button data-testid="sovereign-seal-btn" onClick={generateSeal} disabled={studioBusy === "seal"}
                    className="w-full mt-2 text-sm flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#c9a24a] to-primary text-white rounded-lg py-2 hover:opacity-90 disabled:opacity-60">
                    {studioBusy === "seal" ? <><RefreshCw className="w-4 h-4 animate-spin" /> Minting…</> : <><QrCode className="w-4 h-4" /> {premiumUnlocked ? "Generate Sovereign Seal (physical)" : "Add to Physical Card · Premium"}</>}
                    {!premiumUnlocked && <Crown className="w-3.5 h-3.5 gold-text" />}
                  </button>
                )}
                <DirectorDebugTag requiredTier="Credit Pack" />
                {sealUrl && (
                  <div className="mt-3 flex flex-col items-center" data-testid="seal-qr">
                    <div className="bg-[#f7f2ea] p-3 rounded-xl">
                      <QRCodeCanvas value={sealUrl} size={128} fgColor="#b08a3a" bgColor="#f7f2ea" level="H" includeMargin={false} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5 text-center">Your Sovereign Seal — one code for audio, toast &amp; signatures. Printed centered on the card back.</p>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground/70 mt-1.5">A 30-second voice greeting. Plays on digital cards; Royal unlocks the printed QR.</p>
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{result.occasion} · {result.loved_one_name}</div>
                <div className="flex items-center gap-1">
                  {premiumUnlocked ? (
                    <MagicWand lovedOneId={result.loved_one_id} occasion={result.occasion} highConfidence={!!lovedOnes.find((l) => l.id === result.loved_one_id)?.style_profile} onPick={(t) => setEditMsg(t)} />
                  ) : (
                    <button type="button" data-testid="magic-wand-locked" title="AI Magic Wand — unlocks with any Credit Pack"
                      onClick={() => setUpgradeGate({ tier: "credit_pack", feature: "The AI Magic Wand" })}
                      className="relative w-9 h-9 rounded-full flex items-center justify-center border border-border bg-muted/50 text-muted-foreground hover:bg-muted">
                      <Wand2 className="w-4 h-4" />
                      <Crown className="w-3 h-3 gold-text absolute -top-1.5 -right-1.5" />
                    </button>
                  )}
                  <DirectorDebugTag requiredTier="Credit Pack" />
                </div>
              </div>
              <textarea data-testid="card-message" value={editMsg} onChange={(e) => setEditMsg(e.target.value)} rows={5}
                className="w-full bg-input border border-border rounded-lg px-3 py-2.5 font-serif text-lg leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" />
              <button data-testid="preview-pdf-btn" onClick={async () => { await autoSaveMessage(); setPreviewId(result.id); }}
                className="w-full mt-3 flex items-center justify-center gap-2 surface rounded-lg py-2.5 text-primary hover:border-primary/40 font-medium"><FileText className="w-4 h-4" /> Preview PDF</button>

              {/* Digital Ink signature */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-muted-foreground flex items-center gap-1.5"><PenTool className="w-3.5 h-3.5" /> Your signature</label>
                  {sigSaved && <span className="text-xs text-secondary flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
                </div>
                <SignaturePad ref={padRef} label="Sign here — thickness follows your pressure & speed" />
                <div className="flex gap-2 mt-2">
                  <button data-testid="save-signature-btn" onClick={saveSignature}
                    className="flex-1 text-sm surface rounded-lg py-2 hover:border-primary/40">Save Signature</button>
                  <button data-testid="request-cosign-btn" onClick={requestCosign}
                    className="flex-1 text-sm flex items-center justify-center gap-1.5 surface rounded-lg py-2 text-primary hover:border-primary/40"><Users className="w-4 h-4" /> Request Co-Signature</button>
                </div>
                {cosignLink && (
                  <div className="mt-2 flex items-center gap-2 surface rounded-lg p-2" data-testid="cosign-link-box">
                    <input readOnly value={cosignLink} className="flex-1 bg-transparent text-xs text-muted-foreground truncate outline-none" />
                    <button onClick={copyLink} className="text-xs flex items-center gap-1 text-primary">{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}</button>
                  </div>
                )}
              </div>

              {/* Gift choice: Cash or Charitable Donation */}
              <div className="mt-4 surface rounded-xl p-3" data-testid="gift-choice">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5"><Gift className="w-4 h-4 gold-text" /> Attach a gift</span>
                  {giftSaved && <span className="text-xs text-secondary flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
                </div>
                <div className="flex gap-2 mb-2">
                  {[["none", "None"], ["cash", "Cash"], ["charity", "Charity"]].map(([v, l]) => (
                    <button key={v} data-testid={`gift-${v}`}
                      onClick={() => setGiftType(v)}
                      className={`flex-1 text-xs rounded-lg py-2 border flex items-center justify-center gap-1 ${giftType === v ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {giftType !== "none" && (
                  <div className="space-y-2">
                    {giftType === "cash" ? (
                      // Natural Language Builder — fill-in-the-blanks sentence instead of
                      // separate disconnected fields. Snappier to fill, reads back as a
                      // confirmation of exactly what's about to be charged.
                      <p className="text-base font-serif leading-relaxed" data-testid="natural-language-gift-builder">
                        Sending{" "}
                        <span className="inline-flex items-center">
                          <span className="text-muted-foreground mr-0.5">$</span>
                          <input type="number" data-testid="gift-amount" value={giftAmount}
                            onChange={(e) => setGiftAmount(e.target.value)}
                            className="w-16 bg-transparent border-b-2 border-primary/40 focus:border-primary text-center font-semibold px-0.5 focus:outline-none"
                          />
                        </span>{" "}
                        to <span className="font-semibold text-primary">{result.loved_one_name}</span>{" "}
                        because{" "}
                        <input type="text" data-testid="gift-note" value={giftNote}
                          onChange={(e) => setGiftNote(e.target.value)}
                          placeholder="you mean the world to me"
                          className="w-48 bg-transparent border-b-2 border-primary/40 focus:border-primary px-0.5 focus:outline-none italic placeholder:text-muted-foreground/50"
                        />.
                      </p>
                    ) : (
                      <>
                        <input type="number" data-testid="gift-amount" value={giftAmount} onChange={(e) => setGiftAmount(e.target.value)}
                          className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Amount $" />
                        <select data-testid="gift-charity" value={charityEin} onChange={(e) => setCharityEin(e.target.value)}
                          className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
                          <option value="">Choose a charity…</option>
                          {charities.map((c) => <option key={c.ein} value={c.ein}>{c.name} — {c.cause}</option>)}
                        </select>
                      </>
                    )}
                    <button data-testid="save-gift-btn" onClick={saveGift}
                      className="w-full text-sm flex items-center justify-center gap-1.5 bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90">
                      <HeartHandshake className="w-4 h-4" /> {giftType === "charity" ? "Attach Donation" : "Attach Cash Gift"}
                    </button>
                    <p className="text-[11px] text-muted-foreground/70">A &quot;Scan for Impact/Gift&quot; QR is printed on the card.</p>
                  </div>
                )}
              </div>

              {credits && (
                <div className="mt-4 text-xs text-muted-foreground surface rounded-lg px-3 py-2 flex items-center justify-between" data-testid="credit-status">
                  <span>Physical mailing: {credits.next_physical_source === "credit" ? `${credits.mailing_credits} credit(s)` : "no credits — $9.99/card or buy a pack"}</span>
                  <span className="gold-text">Balance: {credits.mailing_credits}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => saveMessage(result)} data-testid="save-message-btn"
                  className="flex items-center gap-1.5 text-sm bg-muted/50 border border-border px-3 py-2 rounded-lg hover:bg-muted"><Save className="w-4 h-4" /> Save edits</button>
                <button onClick={async () => { await autoSaveMessage(); approve(result, "digital"); }} data-testid="deliver-digital-btn"
                  className="flex items-center gap-1.5 text-sm bg-muted/50 border border-secondary/40 text-secondary px-3 py-2 rounded-lg hover:bg-secondary/10"><Send className="w-4 h-4" /> Deliver Digitally</button>
                <button onClick={async () => { await autoSaveMessage(); approve(result, "physical"); }} data-testid="mail-card-btn"
                  className="flex items-center gap-1.5 text-sm bg-gradient-to-r from-secondary to-primary text-white px-3 py-2 rounded-lg hover:opacity-90"><Truck className="w-4 h-4" /> Print &amp; Mail</button>
              </div>
            </div>
          ) : (
            <div data-testid="showroom-empty-state" className="animate-fade-up">
              {/* The Canvas of Imagination — a faint, animated Maison Seal watermark
                  replaces the plain "will appear here" text with a premium invitation. */}
              <div data-testid="canvas-of-imagination"
                className="rounded-xl overflow-hidden border border-dashed border-border/70 mb-3 relative h-64 flex flex-col items-center justify-center glass-placeholder">
                <div className="maison-seal-watermark absolute inset-0 flex items-center justify-center pointer-events-none">
                  <SealBadge tier="gold" size={168} />
                </div>
                <div className="relative z-10 text-center px-6">
                  <p className="font-serif text-xl text-muted-foreground/80">The Showroom of Infinity.</p>
                  <p className="text-sm text-muted-foreground/60 mt-1 tracking-wide uppercase">Craft to Begin.</p>
                </div>
              </div>

              {/* Teaser Stack — Material Preview, Creative Studio & Audio Keepsake, mirrored
                  from the real post-generation sections but dimmed to a disabled "skeleton"
                  state (opacity-60 + pointer-events-none) so the potential is felt immediately. */}
              <div className="relative" data-testid="teaser-stack">
                <div className="opacity-60 pointer-events-none" data-testid="teaser-dimmed-group">
                  {/* Material Preview teaser */}
                  <div data-testid="material-preview-skeleton" className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-muted-foreground/70 flex items-center gap-1"><Sparkles className="w-3 h-3 gold-text/60" /> Material Preview:</span>
                    <span className="text-xs rounded-full px-3 py-1 glass-placeholder text-muted-foreground/60 capitalize">Foil</span>
                    <span className="text-xs rounded-full px-3 py-1 glass-placeholder text-muted-foreground/60 capitalize">Glass</span>
                  </div>

                  {/* Creative Studio teaser */}
                  <div data-testid="creative-studio-skeleton" className="glass-placeholder rounded-xl p-3 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-1.5 text-muted-foreground/70"><ImageIcon className="w-4 h-4 text-primary/40" /> Creative Studio</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[["AI Only", Sparkles], ["Photo Only", ImagePlus], ["Bespoke Mashup", Crown]].map(([label, Icon]) => (
                        <div key={label} className="text-xs rounded-lg py-3 border border-dashed border-border/50 flex flex-col items-center gap-1 text-muted-foreground/40">
                          <Icon className="w-4 h-4 opacity-50" /> {label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Audio Keepsake teaser */}
                  <div data-testid="audio-studio-skeleton" className="glass-placeholder rounded-xl p-3 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-1.5 text-muted-foreground/70"><Mic className="w-4 h-4 text-primary/40" /> Audio Keepsake</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 text-sm rounded-lg py-2 border border-dashed border-border/50 flex items-center justify-center gap-1.5 text-muted-foreground/40"><Mic className="w-4 h-4 opacity-50" /> Record</div>
                      <div className="flex-1 text-sm rounded-lg py-2 border border-dashed border-border/50 flex items-center justify-center gap-1.5 text-muted-foreground/40"><ImagePlus className="w-4 h-4 opacity-50" /> Upload</div>
                    </div>
                  </div>
                </div>

                {/* Gold overlay hint — floats above the dimmed teasers, fully legible */}
                <div data-testid="teaser-unlock-hint" className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center px-4 z-10">
                  <div className="bg-gradient-to-r from-[#c9a24a] to-[#b08a3a] text-white text-xs font-medium tracking-wide rounded-full px-4 py-2 shadow-lg flex items-center gap-1.5 text-center">
                    <Crown className="w-3.5 h-3.5 shrink-0" /> Craft your card to unlock the Soul of the Maison.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery */}
      {cards.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-2xl mb-4">Your Card Gallery</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="card-gallery">
            {cards.map((c) => (
              <div key={c.id} className="glass rounded-2xl overflow-hidden card-hover">
                <div className="relative">
                  <img src={c.image_thumb_base64 ? `data:image/jpeg;base64,${c.image_thumb_base64}` : `data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-40 object-cover" />
                  <div className="absolute top-2 right-2"><SealBadge tier={c.seal_tier || "gold"} size={32} /></div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.occasion}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted/50 border border-border">{c.delivery?.status || "created"}</span>
                  </div>
                  <div className="font-medium mb-1">{c.loved_one_name}</div>
                  <p className="text-sm text-muted-foreground line-clamp-2 font-serif italic">{c.message}</p>
                  <div className="flex gap-2 mt-3">
                    {(!c.delivery || c.delivery.status === "draft") ? (
                      <>
                        <button onClick={() => approve(c, "digital")} className="flex-1 text-xs flex items-center justify-center gap-1 bg-muted/50 border border-secondary/40 text-secondary rounded-lg py-1.5 hover:bg-secondary/10"><Send className="w-3.5 h-3.5" /> Digital</button>
                        <button onClick={() => approve(c, "physical")} className="flex-1 text-xs flex items-center justify-center gap-1 bg-secondary/20 text-secondary border border-secondary/30 rounded-lg py-1.5 hover:bg-secondary/30"><Truck className="w-3.5 h-3.5" /> Mail</button>
                      </>
                    ) : (
                      <button data-testid="living-capsule-btn" onClick={() => setCapsuleCard(c)}
                        className="flex-1 text-xs flex items-center justify-center gap-1 bg-gradient-to-r from-[#c9a24a] to-primary text-white rounded-lg py-1.5 hover:opacity-90"><Sparkles className="w-3.5 h-3.5" /> Evolve this Legacy</button>
                    )}
                    {c.experience_url && (
                      <button data-testid="gallery-qr-btn" onClick={() => setQrCard(qrCard?.id === c.id ? null : c)}
                        title="The Sovereign Seal — the Key to the AR Pop &amp; Maison Wallet"
                        className="text-xs surface rounded-lg py-1.5 px-2 hover:border-primary/40"><QrCode className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => remove(c.id)} className="text-xs text-muted-foreground hover:text-red-400 px-2 py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {qrCard?.id === c.id && (
                    <div className="mt-3 flex flex-col items-center surface rounded-xl p-3" data-testid="gallery-qr-panel">
                      <div className="bg-[#f7f2ea] p-2 rounded-lg">
                        <QRCodeCanvas value={c.experience_url} size={96} fgColor="#b08a3a" bgColor="#f7f2ea" level="H" includeMargin={false} />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5 text-center">The Sovereign Seal — scan for the AR Pop &amp; Maison Wallet claim.</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <GiftPreview cardId={previewId} open={!!previewId} onClose={() => setPreviewId(null)}
        onSend={previewId && result ? async () => { setPreviewId(null); await autoSaveMessage(); approve(result, "physical"); } : null} />
      <UpgradeModal open={!!upgradeGate} onClose={() => setUpgradeGate(null)} tier={upgradeGate?.tier || "credit_pack"} feature={upgradeGate?.feature || "This feature"} />
      <BoutiqueCheckoutModal open={boutiqueModal} onClose={() => setBoutiqueModal(false)} cardId={boutiqueCardId} />
      <LivingCapsuleModal open={!!capsuleCard} onClose={() => setCapsuleCard(null)} card={capsuleCard} />
      <PurchaseSealOverlay open={!!purchaseSealCard} card={purchaseSealCard} onClose={() => setPurchaseSealCard(null)} />
    </AppShell>
  );
};

export default Cards;
